import React, { useEffect, useState, useRef, Children, Component } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { ArrowRightIcon, BarChart3Icon, FileTextIcon, GlobeIcon, ShieldCheckIcon, ScaleIcon, ScanLineIcon, LayoutDashboardIcon, FolderIcon, SettingsIcon, CheckCircle2Icon, ClockIcon, FactoryIcon, CpuIcon, DownloadIcon, FlameIcon, RadioTowerIcon, LandmarkIcon, PackageIcon, HardHatIcon, WheatIcon, PillIcon, PlayIcon, TrendingDownIcon, ZapIcon, DatabaseIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { HowCarbonSafeWorks } from '../components/HowCarbonSafeWorks';
import { MAILTO } from '../utils/mailto';
/* ═══════════════════════════════════════════════
   HERO IMAGE COMPOSITION
   Layered uploaded images with depth
   ═══════════════════════════════════════════════ */
function HeroImageComposition() {
  return <div className="relative w-full min-h-[480px] lg:min-h-[520px]" data-id="element-2002">
      {/* Main: Corporate team photo */}
      <motion.div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl" initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.2,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-2003">

        <img src="/68820e57-4d9e-40a6-b7b3-9ac66568bb2d.png" alt="Corporate team reviewing carbon data" className="w-full h-auto rounded-2xl" data-id="element-2004" />

      </motion.div>

      {/* Overlapping: Net Zero Tracker card */}
      <motion.div className="absolute -bottom-8 -left-6 z-20 w-[55%] max-w-[280px]" initial={{
      opacity: 0,
      y: 40,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} transition={{
      duration: 0.8,
      delay: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-2005">

        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3zho3j7rbfxjzyaqujg3yf/97e3debd-8536-40d8-be3d-cf5f917b7718/pasted-image.png" alt="Net Zero Tracker showing Scope 1, 2, 3 emissions" className="w-full h-auto rounded-2xl shadow-2xl border border-gray-100" data-id="element-2006" />

      </motion.div>

      {/* Overlapping: Emissions Dashboard card */}
      <motion.div className="absolute -top-6 -right-6 z-20 w-[50%] max-w-[260px]" initial={{
      opacity: 0,
      y: -20,
      x: 20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} transition={{
      duration: 0.8,
      delay: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-2007">

        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3zho3j7rbfxjzyaqujg3yf/97e3debd-8536-40d8-be3d-cf5f917b7718/pasted-image-1.png" alt="Emissions Dashboard with scope breakdown" className="w-full h-auto rounded-2xl shadow-2xl border border-gray-100" data-id="element-2008" />

      </motion.div>
    </div>;
}
/* ═══════════════════════════════════════════════
   ANIMATED STAT
   ═══════════════════════════════════════════════ */
function AnimatedStat({
  value,
  label,
  detail
}: {
  value: string;
  label: string;
  detail: string;
}) {
  const numericMatch = value.match(/^([\d.]+)(.*)$/);
  const numericValue = numericMatch ? parseFloat(numericMatch[1]) : 0;
  const suffix = numericMatch ? numericMatch[2] : value;
  const isNumeric = !!numericMatch;
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, v => {
    if (numericValue < 10) return Number(v.toFixed(1));
    return Math.round(v);
  });
  useEffect(() => {
    if (isInView && isNumeric) {
      const controls = animate(mv, numericValue, {
        duration: 2,
        ease: [0.22, 1, 0.36, 1]
      });
      return controls.stop;
    }
  }, [isInView, numericValue, mv, isNumeric]);
  useEffect(() => {
    const unsub = rounded.on('change', v => setDisplay(v));
    return unsub;
  }, [rounded]);
  return <div ref={ref} className="text-center" data-id="element-2009">
      <p className="text-4xl sm:text-5xl font-bold text-carbon-bright" data-id="element-2010">
        {isNumeric ? `${display.toLocaleString()}${suffix}` : value}
      </p>
      <p className="mt-2 text-base font-bold text-white" data-id="element-2011">{label}</p>
      <p className="mt-1 text-sm text-white/50 leading-relaxed" data-id="element-2012">{detail}</p>
    </div>;
}
/* ═══════════════════════════════════════════════
   SCOPE BREAKDOWN CHART
   ═══════════════════════════════════════════════ */
function ScopeBreakdownChart() {
  return <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-lg" data-id="element-2013">
      <div className="mb-6" data-id="element-2014">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-2015">
          Typical Enterprise Profile
        </p>
        <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-2016">
          GHG Emissions by Scope
        </p>
      </div>

      <div className="space-y-5" data-id="element-2017">
        <div data-id="element-2018">
          <div className="flex items-center justify-between mb-2" data-id="element-2019">
            <div className="flex items-center gap-2" data-id="element-2020">
              <div className="w-3 h-3 rounded-sm bg-carbon-deep" data-id="element-2021" />
              <span className="text-sm font-bold text-carbon-deep" data-id="element-2022">
                Scope 1
              </span>
            </div>
            <span className="text-sm font-bold text-carbon-deep" data-id="element-2023">18%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-2024">
            <motion.div className="h-full rounded-full bg-carbon-deep" initial={{
            width: 0
          }} whileInView={{
            width: '18%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            delay: 0.3,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-2025" />

          </div>
          <p className="text-xs text-gray-400 mt-1.5" data-id="element-2026">
            Direct emissions from owned facilities
          </p>
        </div>

        <div data-id="element-2027">
          <div className="flex items-center justify-between mb-2" data-id="element-2028">
            <div className="flex items-center gap-2" data-id="element-2029">
              <div className="w-3 h-3 rounded-sm bg-carbon-mid" data-id="element-2030" />
              <span className="text-sm font-bold text-carbon-deep" data-id="element-2031">
                Scope 2
              </span>
            </div>
            <span className="text-sm font-bold text-carbon-deep" data-id="element-2032">32%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-2033">
            <motion.div className="h-full rounded-full bg-carbon-mid" initial={{
            width: 0
          }} whileInView={{
            width: '32%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            delay: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-2034" />

          </div>
          <p className="text-xs text-gray-400 mt-1.5" data-id="element-2035">
            Indirect emissions from purchased energy
          </p>
        </div>

        <div data-id="element-2036">
          <div className="flex items-center justify-between mb-2" data-id="element-2037">
            <div className="flex items-center gap-2" data-id="element-2038">
              <div className="w-3 h-3 rounded-sm bg-carbon-bright" data-id="element-2039" />
              <span className="text-sm font-bold text-carbon-deep" data-id="element-2040">
                Scope 3
              </span>
            </div>
            <span className="text-sm font-bold text-carbon-deep" data-id="element-2041">50%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-2042">
            <motion.div className="h-full rounded-full bg-carbon-bright" initial={{
            width: 0
          }} whileInView={{
            width: '50%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            delay: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-2043" />

          </div>
          <p className="text-xs text-gray-400 mt-1.5" data-id="element-2044">
            Value chain emissions (upstream & downstream)
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 p-3 rounded-xl bg-carbon-bright/5 border border-carbon-bright/15" data-id="element-2045">
        <div className="w-8 h-8 rounded-lg bg-carbon-bright/15 flex items-center justify-center flex-shrink-0" data-id="element-2046">
          <BarChart3Icon className="w-4 h-4 text-carbon-bright" data-id="element-2047" />
        </div>
        <p className="text-xs text-gray-500 leading-snug" data-id="element-2048">
          <span className="font-bold text-carbon-deep" data-id="element-2049">
            Scope 3 typically represents 50-80% of total emissions
          </span>
          . CarbonSafe automates calculations across all 15 GHG Protocol
          categories.
        </p>
      </div>
    </div>;
}
/* ═══════════════════════════════════════════════
   INDUSTRY CARD
   ═══════════════════════════════════════════════ */
interface IndustryInfo {
  icon: ComponentType<{
    className?: string;
  }>;
  name: string;
  color: string;
  description: string;
}
function IndustryCard({
  industry,
  index,
  isInView
}: {
  industry: IndustryInfo;
  index: number;
  isInView: boolean;
}) {
  const IconComp = industry.icon;
  return <motion.article className="relative p-5 rounded-2xl border border-gray-100 bg-white overflow-hidden" initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay: 0.08 + index * 0.05,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-2050">

      {/* Watermark background icon */}
      <div className="absolute -bottom-4 -right-4 opacity-[0.04] pointer-events-none select-none" data-id="element-2051">
        <IconComp className="w-28 h-28" data-id="element-2052" />
      </div>

      <div className="relative z-10 flex items-start gap-3.5" data-id="element-2053">
        <div className="w-11 h-11 rounded-xl bg-carbon-bright/15 text-carbon-bright flex items-center justify-center flex-shrink-0" data-id="element-2054">
          <IconComp className="w-5 h-5" data-id="element-2055" />
        </div>
        <div className="min-w-0 flex-1" data-id="element-2056">
          <h3 className="text-sm font-bold text-carbon-deep" data-id="element-2057">
            {industry.name}
          </h3>
          <p className="text-xs text-gray-500 leading-relaxed mt-1.5" data-id="element-2058">
            {industry.description}
          </p>
        </div>
      </div>
    </motion.article>;
}
/* ═══════════════════════════════════════════════
   DASHBOARD MOCKUP (Platform Preview)
   ═══════════════════════════════════════════════ */
function GHGDashboardMockup() {
  const barHeights = [82, 75, 68, 72, 60, 55, 48];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return <div className="rounded-2xl shadow-2xl border border-gray-200 overflow-hidden bg-white" style={{
    transform: 'perspective(1200px) rotateX(2deg) rotateY(-1deg) scale(0.92)'
  }} data-id="element-2059">

      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200" data-id="element-2060">
        <div className="flex gap-1.5" data-id="element-2061">
          <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-2062" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-2063" />
          <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-2064" />
        </div>
        <div className="flex-1 mx-4" data-id="element-2065">
          <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-400 font-mono border border-gray-200 max-w-md" data-id="element-2066">
            app.carbonsafe.io/ghg-reporting
          </div>
        </div>
      </div>

      <div className="flex min-h-[420px]" data-id="element-2067">
        {/* Sidebar */}
        <div className="w-48 bg-carbon-deep flex-shrink-0 p-3 hidden sm:block" data-id="element-2068">
          <div className="flex items-center gap-2 px-2 py-2 mb-4" data-id="element-2069">
            <div className="w-7 h-7 rounded-lg bg-carbon-bright flex items-center justify-center" data-id="element-2070">
              <span className="text-white text-xs font-bold" data-id="element-2071">CS</span>
            </div>
            <span className="text-white text-sm font-bold" data-id="element-2072">CarbonSafe</span>
          </div>
          <nav className="space-y-0.5" data-id="element-2073">
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-white/10 text-white text-xs font-medium" data-id="element-2074">
              <LayoutDashboardIcon className="w-3.5 h-3.5" data-id="element-2075" /> Dashboard
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-2076">
              <BarChart3Icon className="w-3.5 h-3.5" data-id="element-2077" /> Emissions
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-2078">
              <FolderIcon className="w-3.5 h-3.5" data-id="element-2079" /> Facilities
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-2080">
              <FileTextIcon className="w-3.5 h-3.5" data-id="element-2081" /> Reports
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-2082">
              <GlobeIcon className="w-3.5 h-3.5" data-id="element-2083" /> CBAM
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-2084">
              <SettingsIcon className="w-3.5 h-3.5" data-id="element-2085" /> Settings
            </div>
          </nav>
        </div>

        {/* Main content */}
        <div className="flex-1 p-4 sm:p-5 bg-gray-50 overflow-hidden" data-id="element-2086">
          <div className="flex items-center justify-between mb-4" data-id="element-2087">
            <div data-id="element-2088">
              <h3 className="text-sm font-bold text-carbon-deep" data-id="element-2089">
                GHG Inventory | FY 2026
              </h3>
              <p className="text-[11px] text-gray-400 mt-0.5" data-id="element-2090">
                Last updated 8 min ago
              </p>
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold" data-id="element-2091">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-2092" /> CSRD
              Compliant
            </span>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4" data-id="element-2093">
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-2094">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-2095">
                Total Emissions
              </p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-2096">
                12,847{' '}
                <span className="text-[10px] font-medium text-gray-400" data-id="element-2097">
                  tCO₂e
                </span>
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-2098">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-2099">Scope 1</p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-2100">2,312</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-2101">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-2102">Scope 2</p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-2103">4,180</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-2104">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-2105">Scope 3</p>
              <p className="text-lg font-bold text-carbon-bright mt-0.5" data-id="element-2106">
                6,355
              </p>
            </div>
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 mb-4" data-id="element-2107">
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-2108">
              <div className="flex items-center justify-between mb-3" data-id="element-2109">
                <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-2110">
                  Monthly Emissions Trend
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-2111">This week</p>
              </div>
              <div className="flex items-end gap-2 h-24" data-id="element-2112">
                {barHeights.map((h, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-2113">

                    <div className="w-full rounded-t-sm bg-carbon-bright/80" style={{
                  height: `${h}%`
                }} data-id="element-2114" />

                    <span className="text-[9px] text-gray-400" data-id="element-2115">{days[i]}</span>
                  </div>)}
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-2116">
              <div className="flex items-center justify-between mb-3" data-id="element-2117">
                <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-2118">
                  By Scope
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-2119">FY 2026</p>
              </div>
              <div className="space-y-2.5" data-id="element-2120">
                <div data-id="element-2121">
                  <div className="flex justify-between text-[10px] mb-1" data-id="element-2122">
                    <span className="text-gray-500 font-medium" data-id="element-2123">
                      Scope 1 — Direct
                    </span>
                    <span className="font-bold text-carbon-deep" data-id="element-2124">18%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-2125">
                    <div className="h-full bg-carbon-deep rounded-full" style={{
                    width: '18%'
                  }} data-id="element-2126" />

                  </div>
                </div>
                <div data-id="element-2127">
                  <div className="flex justify-between text-[10px] mb-1" data-id="element-2128">
                    <span className="text-gray-500 font-medium" data-id="element-2129">
                      Scope 2 — Energy
                    </span>
                    <span className="font-bold text-carbon-deep" data-id="element-2130">32%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-2131">
                    <div className="h-full bg-carbon-mid rounded-full" style={{
                    width: '32%'
                  }} data-id="element-2132" />

                  </div>
                </div>
                <div data-id="element-2133">
                  <div className="flex justify-between text-[10px] mb-1" data-id="element-2134">
                    <span className="text-gray-500 font-medium" data-id="element-2135">
                      Scope 3 — Value Chain
                    </span>
                    <span className="font-bold text-carbon-deep" data-id="element-2136">50%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-2137">
                    <div className="h-full bg-carbon-bright rounded-full" style={{
                    width: '50%'
                  }} data-id="element-2138" />

                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Activity feed */}
          <div className="bg-white rounded-lg border border-gray-100 overflow-hidden" data-id="element-2139">
            <div className="px-3 py-2 border-b border-gray-100" data-id="element-2140">
              <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-2141">
                Recent Activity
              </p>
            </div>
            <div className="divide-y divide-gray-50" data-id="element-2142">
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-2143">
                <FileTextIcon className="w-3 h-3 text-carbon-bright flex-shrink-0" data-id="element-2144" />
                <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-2145">
                  CSRD Report Q4 generated
                </span>
                <span className="text-[10px] text-gray-400" data-id="element-2146">
                  ESRS E1 Climate
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-2147">
                  <ClockIcon className="w-2.5 h-2.5" data-id="element-2148" /> 15m ago
                </span>
              </div>
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-2149">
                <CheckCircle2Icon className="w-3 h-3 text-emerald-500 flex-shrink-0" data-id="element-2150" />
                <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-2151">
                  Scope 2 data imported
                </span>
                <span className="text-[10px] text-gray-400" data-id="element-2152">
                  UMEME utility bills
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-2153">
                  <ClockIcon className="w-2.5 h-2.5" data-id="element-2154" /> 1h ago
                </span>
              </div>
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-2155">
                <ShieldCheckIcon className="w-3 h-3 text-blue-500 flex-shrink-0" data-id="element-2156" />
                <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-2157">
                  CBAM declaration submitted
                </span>
                <span className="text-[10px] text-gray-400" data-id="element-2158">
                  Cement exports Q3
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-2159">
                  <ClockIcon className="w-2.5 h-2.5" data-id="element-2160" /> 3h ago
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
/* ═══════════════════════════════════════════════
   ICON MAP FOR FEATURES
   ═══════════════════════════════════════════════ */
const iconMap: Record<string, ComponentType<{
  className?: string;
}>> = {
  BarChart3Icon,
  FileTextIcon,
  GlobeIcon,
  ShieldCheckIcon,
  ScaleIcon,
  ScanLineIcon
};
/* ═══════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════ */
const industries: IndustryInfo[] = [{
  icon: FactoryIcon,
  name: 'Manufacturing',
  color: 'bg-slate-600',
  description: 'Cement, beverages, and industrial production. Track Scope 1 emissions from kilns, boilers, and process heat — plus Scope 2 from grid electricity and backup generators.'
}, {
  icon: FlameIcon,
  name: 'Oil & Gas',
  color: 'bg-red-600',
  description: 'Upstream exploration, pipeline operations, and refining. Environmental and Social Impact Assessments, flaring reports, and fugitive emissions tracking.'
}, {
  icon: RadioTowerIcon,
  name: 'Telecommunications',
  color: 'bg-yellow-600',
  description: 'Base stations, data centers, and network infrastructure. Growing Scope 2 emissions from grid electricity and diesel backup generators across thousands of sites.'
}, {
  icon: LandmarkIcon,
  name: 'Financial Services',
  color: 'bg-blue-600',
  description: 'Banks, insurers, and investment firms. Financed emissions (Scope 3, Category 15) from lending and investment portfolios under IFRS S2 and TCFD frameworks.'
}, {
  icon: PackageIcon,
  name: 'FMCG & Consumer Goods',
  color: 'bg-orange-600',
  description: 'Soaps, edible oils, plastics, and packaged goods. Multi-facility manufacturing with complex supply chains requiring full value chain emissions mapping.'
}, {
  icon: HardHatIcon,
  name: 'Construction & Steel',
  color: 'bg-amber-700',
  description: 'Steel processing, roofing, and building materials. Energy-intensive metal fabrication with growing carbon accounting obligations and CBAM exposure for exports.'
}, {
  icon: WheatIcon,
  name: 'Agriculture & Agro-Processing',
  color: 'bg-green-700',
  description: 'Sugar, coffee, tea, and food processing. Biomass burning, land-use change, and cogeneration reporting required under the Climate Change Act.'
}, {
  icon: PillIcon,
  name: 'Pharmaceuticals & Healthcare',
  color: 'bg-purple-600',
  description: 'Drug manufacturing and cold chain logistics. Chemical processing, refrigerant emissions, and energy-intensive cleanroom operations require environmental compliance.'
}];
const features = [{
  icon: 'BarChart3Icon',
  title: 'GHG Protocol Scopes 1-3',
  description: 'Complete greenhouse gas inventory following the GHG Protocol Corporate Standard. Automated Scope 3 calculations across 15 categories with Uganda-specific emission factors.'
}, {
  icon: 'FileTextIcon',
  title: 'IFRS S1 & S2 (ISSB)',
  description: 'Generate sustainability and climate disclosures aligned with IFRS S1 (General) and S2 (Climate). Scenario analysis, transition plans, and physical risk assessments included.'
}, {
  icon: 'GlobeIcon',
  title: 'EU CSRD Compliance',
  description: 'Double materiality assessments, ESRS-aligned reporting, and value chain due diligence. Ready for the European Sustainability Reporting Standards.'
}, {
  icon: 'ScaleIcon',
  title: 'CBAM Reporting',
  description: 'Calculate embedded emissions for goods exported to the EU. Automated CBAM declarations with product-level carbon intensity tracking.'
}, {
  icon: 'ShieldCheckIcon',
  title: 'Uganda Climate Change Act',
  description: 'Comply with the National Climate Change Act 2021 requirements for GHG inventories, climate action plans, and reporting to the Ministry of Water and Environment.'
}, {
  icon: 'ScanLineIcon',
  title: 'Audit-Ready Documentation',
  description: 'Full digital audit trail for NEMA environmental audits. Automated evidence collection, data lineage tracking, and third-party verification support.'
}];
const stats = [{
  value: '$2.4B+',
  label: 'EU-Uganda Trade',
  detail: 'Annual bilateral trade value subject to CBAM and EUDR requirements'
}, {
  value: '150+',
  label: 'Reporting Entities',
  detail: 'Ugandan companies expected to fall under mandatory ESG disclosure by 2027'
}, {
  value: '$47/ton',
  label: 'EU Carbon Price',
  detail: 'CBAM will apply EU carbon pricing to imports from non-pricing jurisdictions'
}, {
  value: '2025',
  label: 'CSRD Deadline',
  detail: 'First wave of EU sustainability reporting standards now in effect'
}];

/* ═══════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════════ */
export function GHGAccounting() {
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, {
    once: true,
    margin: '-60px'
  });
  const impactRef = useRef(null);
  const impactInView = useInView(impactRef, {
    once: true,
    margin: '-80px'
  });
  const industriesRef = useRef(null);
  const industriesInView = useInView(industriesRef, {
    once: true,
    margin: '-60px'
  });
  const featuresRef = useRef(null);
  const featuresInView = useInView(featuresRef, {
    once: true,
    margin: '-80px'
  });
  const platformRef = useRef(null);
  const platformInView = useInView(platformRef, {
    once: true,
    margin: '-60px'
  });
  const ctaRef = useRef(null);
  const ctaInView = useInView(ctaRef, {
    once: true,
    margin: '-60px'
  });
  return <>
      {/* ═══ HERO ═══ */}
      <section className="relative w-full overflow-hidden bg-white" data-id="element-2161">
        <svg className="absolute inset-0 w-full h-full opacity-[0.04]" aria-hidden="true" data-id="element-2162">

          <defs data-id="element-2163">
            <pattern id="ghg-hero-grid" width="50" height="50" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-2164">

              <line x1="0" y1="0" x2="0" y2="50" stroke="#1B4332" strokeWidth="0.5" data-id="element-2165" />

              <line x1="0" y1="0" x2="50" y2="0" stroke="#1B4332" strokeWidth="0.5" data-id="element-2166" />

            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#ghg-hero-grid)" data-id="element-2167" />
        </svg>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36" data-id="element-2168">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-2169">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-2170">

              <p className="text-sm font-semibold uppercase tracking-widest text-carbon-bright mb-4" data-id="element-2171">
                GHG Accounting & ESG Reporting | Uganda
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-2172">
                Automate Your{' '}
                <span className="relative inline-block" data-id="element-2173">
                  <span className="relative z-10" data-id="element-2174">GHG & ESG Reporting</span>
                  <span className="absolute bottom-1 left-0 right-0 h-[40%] bg-carbon-bright/25 -z-0 rounded-sm" aria-hidden="true" data-id="element-2175" />

                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-500 leading-relaxed max-w-xl" data-id="element-2176">
                Uganda's largest enterprises face mounting pressure from IFRS
                Sustainability Standards, EU CSRD, and CBAM — alongside domestic
                climate legislation. CarbonSafe automates Scope 1, 2, and 3
                emissions tracking, ESG reporting, and regulatory compliance in
                one platform.
              </p>
              <div className="mt-5 flex flex-wrap gap-3" data-id="element-2177">
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-2178">
                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-2179" />
                  IFRS S1/S2
                </span>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-2180">
                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-2181" />
                  CSRD Ready
                </span>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-2182">
                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-2183" />
                  CBAM Compliant
                </span>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-2184">
                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-2185" />
                  GHG Protocol
                </span>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-4" data-id="element-2186">
                <a href={MAILTO.ghgDemo} className="inline-flex items-center gap-2 px-7 py-3.5 bg-carbon-bright text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-carbon-bright/20" data-id="element-2187">

                  See CarbonSafe in action
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2188" />
                </a>
                <a href={MAILTO.ghgPlatform} className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-carbon-bright text-carbon-deep text-base font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-2189">

                  Explore The Platform
                </a>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative" data-id="element-2190">

              <HeroImageComposition data-id="element-2191" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section ref={statsRef} className="w-full bg-carbon-deep py-20 sm:py-28" data-id="element-2192">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2193">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={statsInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2194">

            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-2195">
              The ESG compliance imperative
            </h2>
            <p className="mt-4 text-lg text-white/50 max-w-2xl mx-auto" data-id="element-2196">
              Global sustainability regulations are reshaping how Ugandan
              enterprises measure, report, and reduce their environmental
              impact.
            </p>
          </motion.div>
          <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8" initial="hidden" animate={statsInView ? 'visible' : 'hidden'} variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.12
            }
          }
        }} data-id="element-2197">

            {stats.map(stat => <motion.div key={stat.label} variants={{
            hidden: {
              opacity: 0,
              y: 30
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6
              }
            }
          }} data-id="element-2198">

                <AnimatedStat {...stat} data-id="element-2199" />
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <HowCarbonSafeWorks verticalName="GHG accounting" subtext="From data collection to audit-ready reports, four steps to verified emissions management." steps={[{
      icon: 'BarChart3Icon',
      title: 'Data Collection',
      description: 'Automated ingestion from utility bills, fuel records, supply chain data, and IoT sensors across all operations.'
    }, {
      icon: 'CpuIcon',
      title: 'Emissions Calculation',
      description: 'GHG Protocol-aligned calculations for Scope 1, 2, and 3 using Uganda-specific and IPCC emission factors.'
    }, {
      icon: 'FileTextIcon',
      title: 'Report Generation',
      description: 'Auto-generate IFRS S1/S2, CSRD, CBAM, and GHG Protocol reports with full audit trail.'
    }, {
      icon: 'ShieldCheckIcon',
      title: 'Verification & Filing',
      description: 'Third-party verification support and direct submission to regulatory bodies and ESG rating agencies.'
    }]} data-id="element-2200" />


      {/* ═══ SCOPE BREAKDOWN ═══ */}
      <section ref={impactRef} className="w-full bg-white py-24 sm:py-32" data-id="element-2201">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2202">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center" data-id="element-2203">
            <motion.div initial={{
            opacity: 0,
            x: -40
          }} animate={impactInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-2204">

              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3zho3j7rbfxjzyaqujg3yf/97e3debd-8536-40d8-be3d-cf5f917b7718/pasted-image-2.png" alt="Emissions over time by department" className="w-full h-auto rounded-2xl shadow-lg border border-gray-100" data-id="element-2205" />

            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 40
          }} animate={impactInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.7,
            delay: 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-2206">

              <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-6" data-id="element-2207">
                Full-Scope Coverage
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-carbon-deep tracking-tight leading-tight" data-id="element-2208">
                From direct operations to entire value chains
              </h2>
              <p className="mt-6 text-base text-gray-500 leading-relaxed" data-id="element-2209">
                Most Ugandan enterprises only track Scope 1 and 2 emissions —
                missing the 50-80% of their carbon footprint hidden in supply
                chains, logistics, and product use. CarbonSafe's automated Scope
                3 engine maps your entire value chain, from raw material
                extraction to end-of-life disposal.
              </p>
              <div className="mt-10 grid grid-cols-3 gap-6" data-id="element-2210">
                <div data-id="element-2211">
                  <p className="text-3xl sm:text-4xl font-bold text-carbon-bright" data-id="element-2212">
                    15
                  </p>
                  <p className="mt-1 text-sm text-gray-500 font-medium" data-id="element-2213">
                    Scope 3 categories
                  </p>
                </div>
                <div data-id="element-2214">
                  <p className="text-3xl sm:text-4xl font-bold text-carbon-bright" data-id="element-2215">
                    500+
                  </p>
                  <p className="mt-1 text-sm text-gray-500 font-medium" data-id="element-2216">
                    Data sources
                  </p>
                </div>
                <div data-id="element-2217">
                  <p className="text-3xl sm:text-4xl font-bold text-carbon-bright" data-id="element-2218">
                    99.9%
                  </p>
                  <p className="mt-1 text-sm text-gray-500 font-medium" data-id="element-2219">
                    Calculation accuracy
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ INDUSTRIES WE SERVE ═══ */}
      <section ref={industriesRef} className="w-full bg-gray-50 py-24 sm:py-32" data-id="element-2220">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2221">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={industriesInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2222">

            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-6" data-id="element-2223">
              Who We Help
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-2224">
              Industries <span className="text-carbon-bright" data-id="element-2225">we serve</span>
            </h2>
            <p className="mt-5 text-lg text-gray-500 max-w-3xl mx-auto" data-id="element-2226">
              CarbonSafe provides tailored GHG accounting and ESG compliance
              solutions for Uganda's most carbon-intensive and
              regulation-exposed sectors.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-2227">
            {industries.map((industry, i) => <IndustryCard key={industry.name} industry={industry} index={i} isInView={industriesInView} data-id="element-2228" />)}
          </div>

          {/* Regulatory Callout */}
          <motion.div className="mt-14" initial={{
          opacity: 0,
          y: 20
        }} animate={industriesInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.6,
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2229">

            <div className="p-8 rounded-2xl bg-carbon-deep text-white" data-id="element-2230">
              <div className="flex items-start gap-5" data-id="element-2231">
                <div className="w-12 h-12 rounded-xl bg-carbon-bright/20 flex items-center justify-center flex-shrink-0" data-id="element-2232">
                  <ShieldCheckIcon className="w-6 h-6 text-carbon-bright" data-id="element-2233" />
                </div>
                <div className="flex-1" data-id="element-2234">
                  <h3 className="text-lg font-bold" data-id="element-2235">
                    Uganda's Climate Regulatory Landscape
                  </h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed" data-id="element-2236">
                    The National Climate Change Act 2021 mandates GHG
                    inventories for designated entities. NEMA's Environmental
                    Audit Regulations require annual environmental audits for
                    manufacturing facilities. The Uganda Green Growth
                    Development Strategy sets economy-wide emission reduction
                    targets. A carbon tax framework is under active discussion
                    at the Uganda Revenue Authority, expected to affect
                    industrial emitters by 2027.
                  </p>
                  <a href={MAILTO.complianceGuide} className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-carbon-bright hover:text-carbon-light transition-colors" data-id="element-2237">

                    <DownloadIcon className="w-4 h-4" data-id="element-2238" />
                    Download Compliance Guide
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section ref={featuresRef} className="w-full bg-white py-24 sm:py-32" data-id="element-2239">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2240">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={featuresInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2241">

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-2242">
              Comprehensive ESG reporting for{' '}
              <span className="text-carbon-bright" data-id="element-2243">every framework</span>
            </h2>
            <p className="mt-5 text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-2244">
              One platform that speaks the language of every major
              sustainability reporting standard and Ugandan regulation.
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" animate={featuresInView ? 'visible' : 'hidden'} variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-2245">

            {features.map(feature => {
            const IconComponent = iconMap[feature.icon];
            return <motion.article key={feature.title} className="p-8 rounded-2xl border border-gray-100 bg-gray-50" variants={{
              hidden: {
                opacity: 0,
                y: 40
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1]
                }
              }
            }} data-id="element-2246">

                  <div className="w-14 h-14 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-2247">
                    {IconComponent && <IconComponent className="w-7 h-7 text-carbon-bright" data-id="element-2248" />}
                  </div>
                  <h3 className="text-lg font-bold text-carbon-deep mb-2" data-id="element-2249">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed" data-id="element-2250">
                    {feature.description}
                  </p>
                </motion.article>;
          })}
          </motion.div>
        </div>
      </section>

      {/* ═══ PLATFORM PREVIEW ═══ */}
      <section ref={platformRef} className="w-full bg-gray-50 py-24 sm:py-32" data-id="element-2251">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2252">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={platformInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2253">

            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-6" data-id="element-2254">
              Platform Preview
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-2255">
              GHG accounting and ESG reporting in{' '}
              <span className="text-carbon-bright" data-id="element-2256">one dashboard</span>
            </h2>
            <p className="mt-5 text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-2257">
              From emissions data collection to CSRD-compliant reports: measure,
              manage, and disclose with confidence.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 60
        }} animate={platformInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2258">

            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3zho3j7rbfxjzyaqujg3yf/97e3debd-8536-40d8-be3d-cf5f917b7718/pasted-image-1.png" alt="Emissions Dashboard with scope and category breakdown" className="w-full h-auto rounded-2xl shadow-lg border border-gray-100" data-id="element-2259" />

            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3zho3j7rbfxjzyaqujg3yf/97e3debd-8536-40d8-be3d-cf5f917b7718/pasted-image.png" alt="Net Zero Tracker with scope emissions and compliance status" className="w-full h-auto rounded-2xl shadow-lg border border-gray-100" data-id="element-2260" />

          </motion.div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section ref={ctaRef} className="w-full py-24 sm:py-32 bg-white" data-id="element-2261">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2262">
          <motion.div className="relative overflow-hidden rounded-3xl px-8 py-16 sm:px-16 sm:py-20 text-center" style={{
          background: 'linear-gradient(135deg, #2D6A4F 0%, #1B4332 100%)'
        }} initial={{
          opacity: 0,
          y: 40
        }} animate={ctaInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2263">

            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-10" style={{
            background: 'radial-gradient(circle, #52B788, transparent)'
          }} aria-hidden="true" data-id="element-2264" />

            <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-10" style={{
            background: 'radial-gradient(circle, #6BCB77, transparent)'
          }} aria-hidden="true" data-id="element-2265" />

            <h2 className="relative text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight max-w-3xl mx-auto" data-id="element-2266">
              Every ton measured.
              <br data-id="element-2267" />
              <span className="text-carbon-bright" data-id="element-2268">Every report trusted.</span>
            </h2>
            <p className="relative mt-6 text-lg text-white/60 max-w-xl mx-auto" data-id="element-2269">
              Join Uganda's leading enterprises using CarbonSafe to automate GHG
              accounting and meet global ESG reporting standards.
            </p>
            <div className="relative mt-10 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2270">
              <a href={MAILTO.ghgDemo} className="inline-flex items-center gap-2.5 px-8 py-4 bg-carbon-bright text-white text-base font-semibold rounded-full hover:bg-carbon-light transition-colors shadow-lg shadow-black/20" data-id="element-2271">

                Start Your ESG Journey
                <ArrowRightIcon className="w-4 h-4" data-id="element-2272" />
              </a>
              <a href={MAILTO.ghgCaseStudy} className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-full hover:bg-white/10 transition-colors" data-id="element-2273">

                Download Case Study
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-2274" />
    </>;
}